import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";


import {
  Backdrop,
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  makeStyles,
  TextField as TextFieldMui,
  Typography,
  useMediaQuery,
} from "@material-ui/core";

import {
  Edit,
  FileInput,
  FormTab,
  FormDataConsumer,
  Loading,
  RadioButtonGroupInput,
  required,
  SelectInput,
  TabbedForm,
  TextInput,
  Toolbar,
  TopToolbar,
  useEditController,
  useNotify,
} from "react-admin";
import { useForm } from "react-final-form";

import { AttachFileIcon, CreatedAtIcon } from "../../../components/common/iconography";
import { AddressInput } from "../../../components/common/AddressInput";
import { BackButton } from "../../../components/common/BackButton";
import { CustomFields } from "../../../components/common/CustomFields";
import { FileField } from "../../../components/common/FileField";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { YardDateTimeInput } from "../../../components/common/YardDateTimeInput";
import { HelpButton } from "../../../components/common/HelpButton";
import { NotesTab } from "../../../components/common/NotesTab";
import UnlinkButton from "../../../components/common/shipWith/UnlinkButton";

import { logError } from "../../../utils/appUtils";
import {
  ACCEPTED_FILES,
  CarrierType,
  CarrierTypes,
  MAX_FILES,
  OutboundStatus,
  TrueOrFalse,
  Workflow,
} from "../../../utils/constants";
import { handleFileChange } from "../../../utils/fileUtils";
import { resolveScheduledDateTime } from "../../../utils/dateUtils";
import { getBackPath } from "../../../utils/utils";
import { DeepDeleteButton } from "../../../managerApp/resources/outboundShipments/DeepDeleteButton";
import { useUser } from "../../../authenticatorApp/UserContext";
import { Scheduler } from "../../../components/common/scheduler/Scheduler";
import { OutboundEditHelp } from "./OutboundHelp";
import { DialogSaveButton } from "./DialogSaveButton";
import { OutboundAdviceAside } from "./OutboundAdviceAside";
import { PickListTab } from "./OutboundShow";
import { getAddressBook } from "../../../managerApp/resources/outboundShipments/OutboundUtils";
import { resolveDefaultEventType } from "../../../managerApp/resources/eventTypes/EventTypeUtils";
import { fetchSavedAddresses } from "../../../managerApp/resources/accounts/AccountUtils";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

const validateRequired = required();

const fetchEventType = async (record, setEventType, setCanSchedule, notify) => {
  try {
    const { eventType, canSchedule } = await resolveDefaultEventType(
      record?.account?.outboundEventTypeId,
      Workflow.OUTBOUND
    );
    setEventType(eventType);
    setCanSchedule(canSchedule);
  } catch (err) {
    logError("outbounds:OutboundAdviceEdit:fetchEventType", err, notify);
  }
};

const ShipmentSetupTab = ({
  appUser,
  record,
  eventType,
  canSchedule,
  selectedTimeslot,
  setSelectedTimeslot,
  notify,
  ...props
}) => {
  const form = useForm();
  const mdUp = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressBook, setAddressBook] = useState([]);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const CarrierTypesReversed = [].concat(CarrierTypes).reverse();
  const { account } = record;

  useEffect(() => {
    if (record?.accountId) {
      fetchSavedAddresses(
        record?.billToAccountId || record?.accountId,
        notify,
        null,
        setAddressBook
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record]);

  const getSpacing = () => {
    const spacing = 12 - (account?.outboundAdviceInputs?.length || 1) * 2;
    return Math.max(spacing, 0);
  };

  const handleSchedulerOpen = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (canSchedule) setIsCalendarOpen(true);
  };

  const ignoreEvent = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleTimeslot = (timeslot) => {
    const scheduledDate = resolveScheduledDateTime(timeslot, {
      notify,
      scope: "outbounds:OutboundAdviceEdit:handleTimeslot",
    });
    if (!scheduledDate) return;
    form.change("scheduledDateTime", scheduledDate);
    setSelectedTimeslot(timeslot);
  };

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <Grid container direction="column" spacing={1}>
          <Grid container direction="row" spacing={1}>
            <Grid item xs={12} sm={6} md={4}>
              <TextFieldMui
                label="Transload Yard"
                margin="dense"
                variant="outlined"
                fullWidth
                value={appUser?.reloadYard?.name}
                disabled
                helperText={false}
              />
            </Grid>
            {/* <Grid item xs={12} md={4} >
            <SelectInput source="site" label="Site" variant="outlined" fullWidth
              choices={[
                {id:"1", name: "Crescent 1"},
                {id:"2", name: "Crescent 2"},
                {id:"2", name: "Dallas 2"},
              ]}
              helperText={false}
            />
          </Grid> */}
            {account?.outboundAdviceInputs?.includes("scheduledDateTime") && (
              <Grid item xs={12} sm={6} md={4}>
                <YardDateTimeInput
                  source="scheduledDateTime"
                  label="Schedule Pickup"
                  variant="outlined"
                  fullWidth
                  disabled={!canSchedule}
                  onClick={handleSchedulerOpen}
                  onKeyPress={ignoreEvent}
                  validate={validateRequired}
                  options={{
                    readOnly: true,
                    InputProps: {
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <CreatedAtIcon onClick={handleSchedulerOpen} />
                        </InputAdornment>
                      ),
                    },
                  }}
                  helperText={false}
                />
                {canSchedule && (
                  <Scheduler
                    eventType={eventType}
                    workflow={Workflow.OUTBOUND}
                    account={record.account}
                    open={isCalendarOpen}
                    setIsOpen={setIsCalendarOpen}
                    resettable={false}
                    selectedTimeslot={selectedTimeslot}
                    setSelectedTimeslot={handleTimeslot}
                  />
                )}
              </Grid>
            )}
          </Grid>
          <Grid container direction="row" spacing={1}>
            {account?.outboundAdviceInputs?.includes("carrierType") && (
              <Grid item xs={12} sm={6} md={4}>
                <SelectInput
                  source="carrierType"
                  label="Carrier Type"
                  variant="outlined"
                  fullWidth
                  disabled={
                    !record.status.startsWith(OutboundStatus.ADVICE_ALL)
                  }
                  choices={CarrierTypesReversed}
                  validate={validateRequired}
                  helperText={false}
                />
              </Grid>
            )}
            {account?.outboundAdviceInputs?.includes("carrierType") && (
              <Grid item xs={12} sm={6} md={4}>
                <TextInput
                  source="carrierName"
                  label="Carrier Name"
                  variant="outlined"
                  fullWidth
                  disabled={
                    record.carrierName && record.status === OutboundStatus.RELEASED
                  }
                  validate={validateRequired}
                  helperText={false}
                />
              </Grid>
            )}
            {account?.outboundAdviceInputs?.includes("carrierType") && (
              <Grid item xs={12} sm={6} md={4}>
                <TextInput
                  source="railCarNumber"
                  label={
                    formData.carrierType === CarrierType.RAIL
                      ? "Railcar Number"
                      : "Trailer Number"
                  }
                  variant="outlined"
                  fullWidth
                  disabled={
                    record.railCarNumber &&
                    record.status === OutboundStatus.RELEASED
                  }
                  parse={(v) => (v !== "" ? v.toUpperCase() : undefined)}
                  validate={validateRequired}
                  inputProps={{ style: { textTransform: "uppercase" } }}
                  helperText={false}
                />
              </Grid>
            )}
            {formData?.carrierType === CarrierType.TRUCK &&
              account?.outboundAdviceInputs?.includes("mustTarp") && (
                <Grid item xs={12} sm={6} md={4}>
                  <RadioButtonGroupInput
                    source="mustTarp"
                    label="Must Tarp?"
                    row
                    fullWidth
                    validate={
                      formData?.carrierType === CarrierType.TRUCK
                        ? validateRequired
                        : undefined
                    }
                    choices={TrueOrFalse}
                    disabled={record.status === OutboundStatus.RELEASED}
                    style={{ marginTop: 0, marginLeft: 8 }}
                    helperText={false}
                  />
                </Grid>
              )}
          </Grid>
          <Grid container direction="row" spacing={1}>
            {account?.outboundAdviceInputs?.includes("poNumber") && (
              <Grid item xs={12} sm={6} md={4}>
                <TextInput
                  source="poNumber"
                  label="PO Number"
                  variant="outlined"
                  fullWidth
                  parse={(v) => (v !== "" ? v.toUpperCase() : undefined)}
                  disabled={record.status === OutboundStatus.RELEASED}
                  validate={validateRequired}
                  inputProps={{ style: { textTransform: "uppercase" } }}
                />
              </Grid>
            )}
            {formData.carrierType === CarrierType.TRUCK &&
              account?.outboundAdviceInputs?.includes("pickupNumber") && (
                <Grid item xs={12} sm={6} md={4}>
                  <TextInput
                    source="pickupNumber"
                    label="Pickup Number"
                    variant="outlined"
                    fullWidth
                    parse={(v) => v.toUpperCase()}
                    validate={validateRequired}
                    disabled={
                      record.account?.generatePickupNumber ||
                      !record.status.startsWith(OutboundStatus.ADVICE_ALL)
                    }
                  />
                </Grid>
              )}
            {account?.outboundAdviceInputs?.includes("shipTo") && (
              <Grid item xs={12}>
                <Typography
                  gutterBottom
                  variant="overline"
                  color="textSecondary"
                >
                  Consignee Address
                </Typography>
                {getAddressBook(
                  addressBook,
                  null, // billToAccount = account
                  form,
                  selectedAddress,
                  setSelectedAddress
                )}

                {!selectedAddress && (
                  <>
                    <AddressInput
                      source="shipTo"
                      variant="outlined"
                      fullWidth
                      showBusinessName={true}
                      required={true}
                      parse={(v) => (v !== "" ? v : undefined)}
                      disabled={record.status === OutboundStatus.RELEASED}
                      allValues={formData}
                    />
                  </>
                )}
              </Grid>
            )}
          </Grid>
          {account?.outboundAdviceInputs?.includes("outboundCustomFields") && (
            <CustomFields
              {...props}
              recordFields={record.customFields}
              accountFields={record.account.outboundCustomFields}
              disabled={formData.status === OutboundStatus.RELEASED}
              width={4}
            />
          )}
          {mdUp && (
            <Grid container direction="row" spacing={2}>
              {account?.outboundAdviceInputs?.includes("attachments") && (
                <Grid item xs={6}>
                  <FileInput
                    source="attachments"
                    label="ATTACHMENTS"
                    variant="outlined"
                    fullWidth
                    placeholder={
                      <p>
                        <AttachFileIcon fontSize="large" /> <br /> Drop files to
                        upload, or click to select.
                      </p>
                    }
                    accept={ACCEPTED_FILES}
                    multiple
                    maxFiles={MAX_FILES}
                    options={{ maxFiles: MAX_FILES }}
                    onChange={(value) =>
                      handleFileChange("attachments", value, form, notify)
                    }
                  >
                    <FileField source="src" title="title" target="_blank" />
                  </FileInput>
                </Grid>
              )}
              {account?.outboundAdviceInputs?.includes("bolFile") && (
                <Grid item xs={6}>
                  <FileInput
                    source="bolFile"
                    label="BOL FILE"
                    variant="outlined"
                    fullWidth
                    placeholder={
                      <p>
                        <AttachFileIcon fontSize="large" /> <br /> Drop a BOL
                        File to upload, or click to select.
                      </p>
                    }
                    accept={ACCEPTED_FILES}
                    onChange={(value) =>
                      handleFileChange("bolFile", value, form, notify)
                    }
                    disabled={false}
                    helperText={false}
                  >
                    <FileField source="src" title="title" target="_blank" />
                  </FileInput>
                </Grid>
              )}
            </Grid>
          )}
          <Grid item xs={12}>
            <Box mt={getSpacing()}></Box>
          </Grid>
        </Grid>
      )}
    </FormDataConsumer>
  );
};

const EditActions = ({ data, ...props }) => {
  return (
    <TopToolbar>
      <HelpButton>
        <OutboundEditHelp {...props} />
      </HelpButton>
      <UnlinkButton record={data} />
      <BackButton {...props} />
    </TopToolbar>
  );
};

const EditToolbar = ({ record, backPath, selectedTimeslot, ...props }) => {
  const classes = useStyles();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  return (
    <Toolbar style={{ justifyContent: "space-between" }}>
      <Backdrop className={classes.backdrop} open={isSaving || isDeleting}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <DialogSaveButton
        {...props}
        record={record}
        backPath={backPath}
        selectedTimeslot={selectedTimeslot}
        isSaving={isSaving}
        setIsSaving={setIsSaving}
        isDeleting={isDeleting}
      />
      <DeepDeleteButton
        {...props}
        record={record}
        backPath={backPath}
        isSaving={isSaving}
        isDeleting={isDeleting}
        setIsDeleting={setIsDeleting}
      />
    </Toolbar>
  );
};

export const OutboundAdviceEdit = (props) => {
  const { appUser } = useUser();
  const history = useHistory();
  const notify = useNotify();
  const { record } = useEditController(props);
  const [eventType, setEventType] = useState(record?.timeslot?.eventType);
  const [canSchedule, setCanSchedule] = useState(
    Boolean(record?.timeslot?.eventType)
  );
  const [selectedTimeslot, setSelectedTimeslot] = useState();
  const backPath = getBackPath(props);

  useEffect(() => {
    if (record?.timeslot) {
      // Editing an existing booking: keep its event type.
      setSelectedTimeslot(record.timeslot);
      setEventType(record.timeslot.eventType);
      setCanSchedule(Boolean(record.timeslot.eventType));
    } else {
      fetchEventType(record, setEventType, setCanSchedule, notify);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record]);

  if (!record) return <Loading />;
  return (
    <Edit
      {...props}
      title={<ForceTitle title="Edit Outbound Advice" />}
      mutationMode="pessimistic"
      actions={<EditActions {...props} backPath={backPath} />}
      aside={
        <OutboundAdviceAside {...props} backPath={backPath} history={history} />
      }
    >
      <TabbedForm
        variant="outlined"
        submitOnEnter={false}
        toolbar={
          <EditToolbar
            {...props}
            record={record}
            selectedTimeslot={selectedTimeslot}
            backPath={backPath}
          />
        }
        // validate={values => validateOutboundEdit(values)}
      >
        <FormTab label="Outbound Setup">
          <ShipmentSetupTab
            {...props}
            appUser={appUser}
            record={record}
            eventType={eventType}
            canSchedule={canSchedule}
            selectedTimeslot={selectedTimeslot}
            setSelectedTimeslot={setSelectedTimeslot}
            notify={notify}
          />
        </FormTab>
        <FormTab label="Pick List">
          <PickListTab {...props} history={history} backPath={backPath} />
        </FormTab>
        <FormTab label="Notes">
          <NotesTab />
        </FormTab>
      </TabbedForm>
    </Edit>
  );
};
